import React, { useEffect, useState } from 'react';
import FormField from '../UI/FormField';
import SelectableItem from '../common/SelectAbleItems';
import {
  API_ENDPOINT,
  API_URL_ORDER_SERVICE,
  API_URL_USER_SERVICE,
} from '@/api/constant';
import { callApi } from '@/api/callApi';
import { useDebounce } from '@/hooks/useDebounce';
import {
  IUserResponse,
  IUserResponseList,
} from '@/interfaces/userResponse.interface';
import { PageInfo } from '@/interfaces/common.interface';
import { GetFilterUrl, showToastNotification } from '@/utility/helper';
import { svgIcons } from '../UI/SvgIcons';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { RoutesLink } from '@/utility/RouteLinks';
import { PlusIcon } from 'lucide-react';

interface AssignDriverProp {
  isLoading: boolean;
  roleId: string;
  drivers: IUserResponse[];
  pageInfo: PageInfo;
}

interface AssignForm {
  id: string;
  driverId: string;
}

interface IAssignDriver {
  id: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess: () => void;
  driverId?: string;
}

const { CrossIcon } = svgIcons;
const AssignDriver: React.FC<IAssignDriver> = ({
  id,
  setIsOpen,
  onSuccess,
  driverId = '',
}) => {
  const [searchInput, setSearchInput] = useState('');
  const debounceValue = useDebounce(searchInput);
  const [roleId, setRoleId] = useState();
  const [states, setStates] = useState<AssignDriverProp>({
    isLoading: false,
    roleId: '',
    drivers: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchRoles = async () => {
    try {
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.roles}/search?query=driver`;
      const res = await callApi(PATH, { method: 'GET' });
      setRoleId(res[0].id);
    } catch (error) {
      // console.log('error in role fetch', error)
    }
  };

  const fetchDrivers = async (debounceValue: string) => {
    try {
      if (!roleId) return;
      const filterQuery = {
        fullName: debounceValue,
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        roleId: roleId,
      };
      handleChangeState({ isLoading: true });
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/list?${query ? `${query}` : ''}`;
      const res = await callApi<IUserResponseList>(PATH, { method: 'GET' });
      handleChangeState({ drivers: res.data, pageInfo: res.pageInfo });
    } catch (error) {
      // console.log('error in driver fetch', error)
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchDrivers(debounceValue);
  }, [debounceValue, roleId]);
  useEffect(() => {
    fetchRoles();
  }, []);

  const methods = useForm<AssignForm>({
    defaultValues: {
      id: id,
      driverId: driverId,
    },
  });

  const { handleSubmit } = methods;

  const onSubmit = async (data: AssignForm) => {
    try {
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/assign-driver`;
      await callApi(PATH, { method: 'PATCH', data });
      onSuccess();
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      // console.log('error ins assign driver', error)
    }
  };

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  return (
    <>
      {/* Modal */}
      <div className="w-full h-screen fixed inset-0 bg-black/50 z-50">
        <div className="w-150 h-80 rounded-xl top-20 absolute left-1/2 -translate-x-1/2 bg-black">
          <div className="bg-white border border-gray-300 drop-shadow-xl z-20 w-full min-h-full p-4 rounded-md">
            <div className="mb-6 relative flex items-center justify-between">
              <h2 className="text-xl font-medium">Assign a Driver</h2>
              <div className="flex gap-1">
                <Link
                  to={RoutesLink.addDriver}
                  className="flex text-sm px-3 bg-red-600 rounded-md text-white font-bold p-1"
                >
                  <span className="flex items-center gap-1">
                    Add Driver <PlusIcon size={18} />
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    (setIsOpen(false), onSuccess());
                  }}
                  className="button__ico uk-modal-close p-1"
                >
                  <span>{CrossIcon}</span>
                </button>
              </div>
            </div>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <FormField
                  placeholder="Search For Drivers"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <SelectableItem
                  loading={states.isLoading}
                  items={states.drivers}
                  isMutiSelect={false}
                  getId={(item) => item.id}
                  fieldName="driverId"
                  emptyMessage="Drivers not found"
                  renderItem={(item) => (
                    <div className="flex flex-col">
                      <span>{item.fullName}</span>
                      <span className="text-sm">email: {item.email}</span>
                    </div>
                  )}
                />
                <div className="flex justify-end gap-3 mt-5">
                  <button
                    onClick={() => setIsOpen(false)}
                    type="button"
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Save
                  </button>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignDriver;
